import React, { useEffect, useState } from "react";
import api, { formatErr } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Trash2, Loader2 } from "lucide-react";
import { toast } from "sonner";

/**
 * cars: array af biler der skal slettes (1 eller flere). Når længde===1 bekræfter
 * brugeren ved at taste nummerpladen ordret. Ved flere skal de skrive "SLET".
 */
export default function DeleteCarDialog({ cars, onClose, onDeleted }) {
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    setConfirmText("");
    setLoading(false);
  }, [cars]);

  if (!cars || cars.length === 0) return null;

  const isMulti = cars.length > 1;
  const requiredText = isMulti ? "SLET" : (cars[0].license_plate || "");
  const matches = confirmText.trim().toUpperCase() === requiredText.toUpperCase();

  const submit = async () => {
    if (!matches) return;
    setLoading(true);
    let okCount = 0;
    let txTotal = 0, inspTotal = 0, taskTotal = 0;
    const failed = [];
    for (const car of cars) {
      try {
        const { data } = await api.delete(`/cars/${car.id}`);
        okCount += 1;
        const d = data?.deleted || {};
        txTotal += d.transactions || 0;
        inspTotal += d.inspections || 0;
        taskTotal += d.tasks || 0;
      } catch (e) {
        failed.push(`${car.license_plate}: ${formatErr(e)}`);
      }
    }
    if (okCount > 0) {
      const extras = [];
      if (txTotal) extras.push(`${txTotal} transaktioner`);
      if (inspTotal) extras.push(`${inspTotal} kontroller`);
      if (taskTotal) extras.push(`${taskTotal} opgaver`);
      const subj = okCount === 1 ? cars[0].license_plate : `${okCount} biler`;
      toast.success(
        `${subj} slettet permanent` + (extras.length ? ` · også slettet: ${extras.join(", ")}` : "")
      );
    }
    if (failed.length) {
      toast.error("Fejl: " + failed.join(" · "));
    }
    if (failed.length === 0) {
      onDeleted();
    } else {
      setLoading(false);
    }
  };

  return (
    <Dialog open={true} onOpenChange={(v) => !v && !loading && onClose()}>
      <DialogContent className="rounded-none max-w-md border-red-900/60" data-testid="delete-car-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tighter text-2xl text-red-400">
            {isMulti ? `Slet ${cars.length} biler permanent` : "Slet bil permanent"}
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-4 text-sm">
          {isMulti ? (
            <div className="text-zinc-300 space-y-2">
              <div>Du er ved at slette {cars.length} biler:</div>
              <ul className="font-mono text-xs bg-zinc-900/60 border border-zinc-800 p-2 max-h-32 overflow-y-auto">
                {cars.map((c) => (
                  <li key={c.id} className="text-white">
                    {c.license_plate}
                    <span className="text-zinc-500 ml-2 font-sans">
                      {[c.make, c.model].filter(Boolean).join(" ")}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-zinc-300">
              Du er ved at slette <span className="font-mono font-bold text-white">{cars[0].license_plate}</span>{" "}
              <span className="text-zinc-500">({[cars[0].make, cars[0].model].filter(Boolean).join(" ") || "uden mærke"})</span>.
            </p>
          )}
          <div className="border border-red-900/60 bg-red-950/30 p-3 text-red-200 text-xs space-y-1">
            <div className="font-semibold uppercase tracking-wider text-red-300">Dette kan ikke fortrydes</div>
            <div>Bilen{isMulti ? "erne" : ""}, alle dens transaktioner, kontroller og åbne opgaver slettes for altid.</div>
            <div>Vil du tilføje den igen, skal du oprette den helt forfra.</div>
          </div>
          <div className="space-y-2">
            <Label className="text-xs uppercase tracking-wider text-zinc-400">
              {isMulti
                ? <>Skriv <span className="font-mono text-white">SLET</span> for at bekræfte</>
                : <>Skriv nummerpladen <span className="font-mono text-white">{requiredText}</span> for at bekræfte</>}
            </Label>
            <Input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value.toUpperCase())}
              className="rounded-none border-zinc-700 uppercase font-mono"
              placeholder={requiredText}
              autoFocus
              data-testid="delete-car-confirm-input"
            />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button
            type="button"
            onClick={onClose}
            disabled={loading}
            className="rounded-none bg-zinc-800 hover:bg-zinc-700 text-white"
            data-testid="delete-car-cancel"
          >
            Annullér
          </Button>
          <Button
            type="button"
            onClick={submit}
            disabled={!matches || loading}
            className="rounded-none bg-red-600 hover:bg-red-500 disabled:opacity-40 disabled:cursor-not-allowed text-white font-semibold"
            data-testid="delete-car-confirm"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Trash2 className="w-4 h-4 mr-2" />}
            Slet permanent
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
